/**
 * What stands between this register and being relied on, per matter.
 *
 * A tier and a count, never a score. The tiers say what blocks what — a register with no official
 * source cannot be researched at all, so telling its reader that a version lacks a citation is
 * noise — and they are not comparable quantities, so the count is within the most blocking tier
 * and never a sum across them. Adding them would invent a scale, and the one number everybody
 * wants here, "how complete is this register", has no denominator: there is no list of the rules a
 * register ought to hold.
 *
 * `saysNothing` is the state that matters most and reads as the quietest: no findings, because
 * nothing could be established. A register nobody can judge must not look like a register with
 * nothing wrong.
 */

import { Link } from 'react-router';
import { Pill } from '~/ui/Pill';
import type { Tone } from '~/ui/tone';
import { routes } from '~/lib/routes';
import type { GapTier, JurisdictionRow, VocabularyTerm } from '~/api/schema';
import styles from './Register.module.css';

const TIER_SHORT: Record<GapTier, string> = {
  cannot_be_worked: 'Blocked',
  holds_rules_it_cannot_answer_with: 'Faulty',
  answers_without_evidence: 'Unevidenced',
};

const TIER_TONE: Record<GapTier, Tone> = {
  cannot_be_worked: 'issue',
  holds_rules_it_cannot_answer_with: 'open',
  answers_without_evidence: 'quest',
};

const TIER_TITLE: Record<GapTier, string> = {
  cannot_be_worked: 'Research cannot run here, or nothing is held.',
  holds_rules_it_cannot_answer_with: 'Rules are held that do not work.',
  answers_without_evidence: 'It answers, and rests on nothing anybody can read.',
};

export interface GapCellProps {
  row: JurisdictionRow;
  matters: readonly VocabularyTerm[];
}

export function GapCell({ row, matters }: GapCellProps) {
  return (
    <div className={styles.gapCell}>
      {matters.map((matter) => {
        const summary = row.matters.find((entry) => entry.matter === matter.code);
        if (!summary) {
          return (
            <span key={matter.code} className={styles.gapNone} title="This register does not handle this matter.">
              {matter.label}: not covered
            </span>
          );
        }
        const gaps = summary.gaps;
        if (!gaps) return null;

        if (gaps.tier === null || gaps.tier === undefined) {
          return (
            <span
              key={matter.code}
              className={styles.gapNone}
              title={
                gaps.saysNothing
                  ? 'No findings — because nothing could be established about this register.'
                  : 'Every check that could run found nothing.'
              }
            >
              {matter.label}: {gaps.saysNothing ? 'nothing established' : 'no findings'}
            </span>
          );
        }

        return (
          <Link
            key={matter.code}
            to={routes.gaps(row.code, matter.code)}
            className={styles.gapLink}
            title={TIER_TITLE[gaps.tier]}
          >
            <span className={styles.gapMatter}>{matter.label}</span>
            <Pill tone={TIER_TONE[gaps.tier]}>
              {TIER_SHORT[gaps.tier]} {gaps.openItems}
            </Pill>
          </Link>
        );
      })}
    </div>
  );
}
